// Per-dialect synthesizer for the alter-column op (v2 Slices A1 + A2: column
// type / nullability / default ALTER, plus identity-sequence restart).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice A1, § Slice A2.
//
// Each engine re-states the column shape per its native idiom:
//   - mssql:  ALTER COLUMN re-spec; defaults are named CONSTRAINT objects so
//             a default change emits separate ADD/DROP CONSTRAINT statements.
//             Identity changes are not in scope (DBCC CHECKIDENT lives on the
//             query-execute path) — the synthesizer rejects with a hint.
//   - pgsql:  one ALTER COLUMN clause per changed field, joined into N
//             ALTER TABLE statements (default off = no coalescing per DSE2-7).
//             Identity restart maps to `ALTER COLUMN col RESTART WITH n`.
//   - mysql:  single MODIFY COLUMN re-stating the entire column spec; the
//             caller must always supply 'change.type'. Identity changes
//             route to slice E3's table-level AUTO_INCREMENT — rejected here.
//   - oracle: single MODIFY (col …) clause; per-field optional. DROP DEFAULT
//             expressed as `DEFAULT NULL` (no explicit DROP DEFAULT syntax).
//             Identity restart requires ALTER SEQUENCE (out of scope) —
//             rejected with a hint to the consumer's query-execute path.
//   - sqlite: not supported on ALTER → `DdlEditError` with
//             `code: 'engine-does-not-support-op'`. Caller may opt into the
//             CREATE-COPY-RENAME-DROP recreate path via `sqliteRecreate: true`
//             — but only by passing the table's CREATE TABLE statement as the
//             source; the recreate planner rejects an ALTER TABLE source.

import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import type { ColumnChange } from '../../types';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent } from './dialect';
import { type ExistingNameOrigin, nameExistingColumn } from './dialect.quoting';

export interface AlterColumnSynthOpts {
  /** The DSE6 STYLE flag — the host relation's own quoting, mirrored onto the
   *  column token. A style may ADD delimiters; it may never decide WHICH
   *  column is named. See `columnNameOrigin`. */
  readonly quoteName: boolean;
  /** WHERE the spelling in `columnName` came from. An ALTER COLUMN names a
   *  column the catalog ALREADY HOLDS — exactly like a rename's old name and a
   *  drop's target — so it sits on the same three-way contract
   *  (`dialect.quoting.ts`'s {@link ExistingNameOrigin}) and `quoteName` alone
   *  cannot answer it. Delimiting a genuinely bare `Target_Col` under a quoted
   *  host asks PostgreSQL for a column it does not have (it holds
   *  `target_col`), and leaving a source-delimited `"Id"` bare under a bare
   *  host asks for `id`. Defaults to `'bare-reference'`, the contract a caller
   *  holding only a name is under. */
  readonly columnNameOrigin?: ExistingNameOrigin;
  readonly mssqlExistingDefaultConstraintName?: string;
  readonly mssqlNewDefaultConstraintName?: string;
}

function isEmpty(change: ColumnChange): boolean {
  return change.type === undefined
    && change.nullable === undefined
    && change.default === undefined
    && change.identity === undefined;
}

function tableText(engine: SqlEngine, table: QualifiedName): string {
  if (table.raw && table.raw.length > 0) return table.raw;
  const name = maybeQuoteIdent(engine, table.name, table.quoted);
  if (table.schema) {
    const schema = maybeQuoteIdent(engine, table.schema, table.quoted);
    return `${schema}.${name}`;
  }
  return name;
}

function nullClause(nullable: boolean | undefined): string {
  if (nullable === true) return ' NULL';
  if (nullable === false) return ' NOT NULL';
  return '';
}

export function synthesizeAlterColumn(
  engine: SqlEngine,
  table: QualifiedName,
  columnName: string,
  change: ColumnChange,
  opts: AlterColumnSynthOpts,
): string {
  if (isEmpty(change)) {
    throw new DdlEditError(
      'invalid-edit',
      `applyDdlEdit: alter-column requires at least one field in 'change' `
        + `(type / nullable / default) — got an empty change object`,
    );
  }
  if (engine === 'sqlite') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: SQLite does not support ALTER TABLE … ALTER COLUMN. `
        + `Pass the table's CREATE TABLE statement (not an ALTER TABLE) with `
        + `'sqliteRecreate: true' for the CREATE-COPY-RENAME-DROP recreate `
        + `path instead.`,
    );
  }
  const tbl = tableText(engine, table);
  // `columnName` is a spelling the catalog ALREADY holds, so the text and the
  // delimit decision are taken together — adding delimiters to a bare
  // reference suppresses the very fold that was resolving it, and folding a
  // spelling the server already stores corrupts it.
  const { text, delimit } = nameExistingColumn(
    columnName, engine, opts.columnNameOrigin ?? 'bare-reference', opts.quoteName,
  );
  const col = maybeQuoteIdent(engine, text, delimit);
  if (engine === 'mssql') return synthesizeMssql(tbl, col, change, opts);
  if (engine === 'postgres') return synthesizePgsql(tbl, col, change);
  if (engine === 'mysql') return synthesizeMysql(tbl, col, change);
  return synthesizeOracle(tbl, col, change);
}

// ─── mssql ────────────────────────────────────────────────────────────────

function synthesizeMssql(
  tbl: string,
  col: string,
  change: ColumnChange,
  opts: AlterColumnSynthOpts,
): string {
  if (change.identity !== undefined) {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: mssql does not support changing identity columns via `
        + `ALTER TABLE … ALTER COLUMN — restart the seed via DBCC CHECKIDENT `
        + `from the consumer's query-execute path`,
    );
  }
  const stmts: string[] = [];

  if (change.type !== undefined || change.nullable !== undefined) {
    if (change.type === undefined) {
      throw new DdlEditError(
        'invalid-edit',
        `applyDdlEdit: mssql ALTER COLUMN requires the column type — supply `
          + `'change.type' even when only changing nullability`,
      );
    }
    stmts.push(
      `ALTER TABLE ${tbl} ALTER COLUMN ${col} ${change.type}${nullClause(change.nullable)}`,
    );
  }

  if (change.default !== undefined) {
    const existing = opts.mssqlExistingDefaultConstraintName;
    if (change.default.kind === 'drop') {
      if (!existing) {
        throw new DdlEditError(
          'invalid-edit',
          `applyDdlEdit: mssql DROP DEFAULT requires `
            + `'mssqlExistingDefaultConstraintName' — defaults on mssql are `
            + `named constraint objects`,
        );
      }
      stmts.push(`ALTER TABLE ${tbl} DROP CONSTRAINT ${existing}`);
    } else {
      if (existing) stmts.push(`ALTER TABLE ${tbl} DROP CONSTRAINT ${existing}`);
      const newName = opts.mssqlNewDefaultConstraintName;
      const constraintPrefix = newName ? `CONSTRAINT ${newName} ` : '';
      stmts.push(
        `ALTER TABLE ${tbl} ADD ${constraintPrefix}DEFAULT (${change.default.expr}) FOR ${col}`,
      );
    }
  }

  return stmts.join(';\n');
}

// ─── postgres ─────────────────────────────────────────────────────────────

function synthesizePgsql(tbl: string, col: string, change: ColumnChange): string {
  const stmts: string[] = [];
  if (change.type !== undefined) {
    stmts.push(`ALTER TABLE ${tbl} ALTER COLUMN ${col} TYPE ${change.type}`);
  }
  if (change.nullable === false) {
    stmts.push(`ALTER TABLE ${tbl} ALTER COLUMN ${col} SET NOT NULL`);
  } else if (change.nullable === true) {
    stmts.push(`ALTER TABLE ${tbl} ALTER COLUMN ${col} DROP NOT NULL`);
  }
  if (change.default !== undefined) {
    if (change.default.kind === 'drop') {
      stmts.push(`ALTER TABLE ${tbl} ALTER COLUMN ${col} DROP DEFAULT`);
    } else {
      stmts.push(`ALTER TABLE ${tbl} ALTER COLUMN ${col} SET DEFAULT ${change.default.expr}`);
    }
  }
  if (change.identity !== undefined) {
    stmts.push(
      `ALTER TABLE ${tbl} ALTER COLUMN ${col} RESTART WITH ${change.identity.start}`,
    );
  }
  return stmts.join(';\n');
}

// ─── mysql ────────────────────────────────────────────────────────────────

function synthesizeMysql(tbl: string, col: string, change: ColumnChange): string {
  if (change.identity !== undefined) {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: mysql does not support changing AUTO_INCREMENT via `
        + `ALTER TABLE … MODIFY COLUMN restart-with — run a table-level `
        + `'ALTER TABLE … AUTO_INCREMENT = N' statement instead`,
    );
  }
  if (change.type === undefined) {
    throw new DdlEditError(
      'invalid-edit',
      `applyDdlEdit: mysql MODIFY COLUMN requires the full type — supply `
        + `'change.type' for any alter-column edit (mysql re-states the `
        + `entire column spec)`,
    );
  }
  let s = `ALTER TABLE ${tbl} MODIFY COLUMN ${col} ${change.type}${nullClause(change.nullable)}`;
  if (change.default && change.default.kind === 'set') {
    s += ` DEFAULT ${change.default.expr}`;
  }
  return s;
}

// ─── oracle ───────────────────────────────────────────────────────────────

function synthesizeOracle(tbl: string, col: string, change: ColumnChange): string {
  if (change.identity !== undefined) {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: oracle does not support restarting an identity column via `
        + `ALTER TABLE … MODIFY — alter the underlying sequence directly via `
        + `ALTER SEQUENCE from the consumer's query-execute path, or DROP + `
        + `re-add the identity (out of scope for slice A2)`,
    );
  }
  let body = col;
  if (change.type !== undefined) body += ` ${change.type}`;
  if (change.default !== undefined) {
    body += change.default.kind === 'drop'
      ? ' DEFAULT NULL'
      : ` DEFAULT ${change.default.expr}`;
  }
  body += nullClause(change.nullable);
  return `ALTER TABLE ${tbl} MODIFY (${body})`;
}
